import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { FieldReportFullViewFilterContextProps, FieldReportFullViewFilterContextType } from 'af-root/context/fieldReportFullViewFilterContext';

import AdditionalFilter from 'af-components/Table/Filters/AdditionalFilter/Multiselect';

import * as AccountActions from 'af-actions/accounts';

export interface ProjectManagerFilterOption {
	id: number;
	label: string;
}

interface OwnProps {
	onChange: (options: ProjectManagerFilterOption[]) => void;
	selected: ProjectManagerFilterOption[];
	forBulkModal?: boolean;
	onClear?: () => void;
	autoOpen?: boolean;
	fieldReportFullViewFilterContext: FieldReportFullViewFilterContextType;
}

type Props = OwnProps & ConnectedProps<typeof connector> & FieldReportFullViewFilterContextProps;

const ProjectManagerFilter: React.FC<Props> = (props) => {

	const {
		onChange,
		findAllForFieldReportFilter,
		selected,
		forBulkModal,
		onClear,
		autoOpen,
		fieldReportFullViewFilterContext,
	} = props;

	const [options, setOptions] = React.useState<ProjectManagerFilterOption[] | null>(null);

	const fetchOptions = React.useCallback(async () => {
		const _options = await findAllForFieldReportFilter();
		const formattedOptions = _options.projectManagers
			.map((option) => ({ id: option.id, label: `${option.fullName} (${option.uniqueId})` }))
			.sort((_a, _b) => {
				if (_a.label < _b.label) {
					return -1;
				}
				if (_a.label > _b.label) {
					return 1;
				}
				return 0;
			});
		setOptions(formattedOptions);
		fieldReportFullViewFilterContext.setProjectManagerOptions(formattedOptions);
	}, [fieldReportFullViewFilterContext, findAllForFieldReportFilter]);

	React.useEffect(() => {
		if (!fieldReportFullViewFilterContext.projectManagerOptions.length) {
			fetchOptions();
		} else {
			setOptions(fieldReportFullViewFilterContext.projectManagerOptions);
		}
	}, [fetchOptions, fieldReportFullViewFilterContext.projectManagerOptions, findAllForFieldReportFilter]);

	const onValueChange = React.useCallback((_options: ProjectManagerFilterOption[]) => {
		onChange(_options);
	}, [onChange]);

	return (
		<AdditionalFilter
			autoOpen={!!autoOpen}
			filterName="Project Manager"
			forBulkModal={!!forBulkModal}
			hasNoneAsOption={true}
			onChange={onValueChange}
			onRemoveFilter={onClear}
			options={options}
			selected={selected}
		/>
	);
};

function mapDispatchToProps() {
	return {
		findAllForFieldReportFilter: AccountActions.findAllProjectManagersForFieldReportFilter,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(ProjectManagerFilter);
